import React, { useEffect, useRef } from 'react';

interface PayPalHostedButtonProps {
  hostedButtonId: string;
  onApprove?: (data: any) => void;
  onError?: (error: any) => void;
  className?: string;
}

declare global {
  interface Window {
    paypal?: any;
  }
}

export const PayPalHostedButton: React.FC<PayPalHostedButtonProps> = ({
  hostedButtonId,
  onApprove,
  onError,
  className = ''
}) => {
  const containerId = `paypal-container-${hostedButtonId}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef(false);

  useEffect(() => {
    // Skip if already rendered
    if (renderedRef.current) {
      return;
    }

    // PayPal's recommended approach: Use DOMContentLoaded event
    const renderButton = () => {
      // Check if PayPal SDK is loaded
      if (!window.paypal || !window.paypal.HostedButtons) {
        console.log(`[PayPal] SDK not ready yet for button ${hostedButtonId}`);
        return false;
      }

      const container = document.getElementById(containerId);
      if (!container) {
        console.error(`[PayPal] Container #${containerId} not found`);
        if (onError) {
          onError(new Error(`Container #${containerId} not found`));
        }
        return false;
      }

      // Clear container first
      container.innerHTML = '';

      try {
        // PayPal's exact approach (Part 2 from their documentation)
        window.paypal.HostedButtons({
          hostedButtonId: hostedButtonId
        })
        .render(`#${containerId}`)
        .then(() => {
          console.log(`✅ PayPal Hosted Button ${hostedButtonId} rendered successfully`);
          renderedRef.current = true;
        })
        .catch((error: any) => {
          console.error(`❌ PayPal Hosted Button ${hostedButtonId} render error:`, error);
          renderedRef.current = false;
          if (onError) {
            onError(error);
          }
        });
        return true;
      } catch (error: any) {
        console.error(`❌ PayPal Hosted Button ${hostedButtonId} initialization error:`, error);
        renderedRef.current = false;
        if (onError) {
          onError(error);
        }
        return false;
      }
    };

    // Wait for DOMContentLoaded (PayPal's recommended approach)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        // Wait for PayPal SDK to load
        const checkInterval = setInterval(() => {
          if (renderButton()) {
            clearInterval(checkInterval);
          }
        }, 100);

        // Timeout after 15 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!renderedRef.current) {
            console.error(`[PayPal] Button ${hostedButtonId} render timeout`);
            if (onError) {
              onError(new Error('PayPal button failed to load. Please refresh the page.'));
            }
          }
        }, 15000);
      });
    } else {
      // DOM already ready, wait for PayPal SDK
      const checkInterval = setInterval(() => {
        if (renderButton()) {
          clearInterval(checkInterval);
        }
      }, 100);

      // Timeout after 15 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!renderedRef.current) {
          console.error(`[PayPal] Button ${hostedButtonId} render timeout`);
          if (onError) {
            onError(new Error('PayPal button failed to load. Please refresh the page.'));
          }
        }
      }, 15000);
    }

    // Cleanup
    return () => {
      renderedRef.current = false;
    };
  }, [hostedButtonId, containerId, onApprove, onError]);

  // Part 3: Container div with exact ID format PayPal expects
  return (
    <div 
      id={containerId}
      ref={containerRef}
      className={className}
      style={{ 
        minHeight: '48px', 
        width: '100%',
        display: 'block'
      }}
    ></div>
  );
};

// Also export as default for compatibility
export default PayPalHostedButton;
